/**
 * Utility functions for WhatsApp sharing
 */

/**
 * Format phone number for WhatsApp (handles Morocco country code +212)
 * @param {string} phone - Raw phone number
 * @returns {string} - Formatted phone number with country code
 */
export function formatPhoneNumber(phone) {
  if (!phone) return null;

  // Remove spaces, dashes, and other non-digit characters except +
  let formatted = phone.replace(/[^\d+]/g, "");

  // Remove leading 0 and add country code if needed (Morocco: +212)
  if (formatted.startsWith("0")) {
    formatted = "+212" + formatted.substring(1);
  } else if (!formatted.startsWith("+")) {
    // If no + prefix, assume it's a local number and add +212
    formatted = "+212" + formatted;
  }

  return formatted;
}

/**
 * Format amount with spaces for readability (e.g., 1234.56 -> 1 234.56)
 * @param {number|string} amount - Amount to format
 * @returns {string} - Formatted amount string
 */
export function formatAmount(amount) {
  const num = parseFloat(amount || 0);
  return num.toFixed(2).replace(/(\d)(?=(\d{3})+(?!\d))/g, "$1 ");
}

/**
 * Build customer report message for WhatsApp
 * @param {Object} params - Report data parameters
 * @param {Object} params.customer - Customer object with name and phone
 * @param {Object} params.filter - Filter object with startDate, endDate, paymentStatus
 * @param {Array} params.reports - Array of report/invoice objects
 * @param {Array} params.payments - Array of payment objects
 * @param {Array} params.creditBalances - Array of credit balance objects
 * @param {number} params.totalPriceTTC - Total price TTC
 * @param {number} params.totalPaid - Total paid amount
 * @param {number} params.totalUnpaid - Total unpaid amount
 * @param {number} params.totalReportPayments - Total report payments
 * @returns {string} - Formatted message for WhatsApp
 */
export function buildCustomerReportMessage({
  customer,
  filter,
  reports,
  payments,
  creditBalances,
  totalPriceTTC,
  totalPaid,
  totalUnpaid,
  totalReportPayments,
}) {
  const customerName =
    (customer && (customer.society || customer.fullName)) || "Client";
  const startDate = (filter && filter.startDate) || "N/A";
  const endDate = (filter && filter.endDate) || "N/A";
  const paymentStatus = (filter && filter.paymentStatus) || "--";

  let message = `👋 Bonjour *${customerName}*,\n\n`;
  message += `📊 *RAPPORT CLIENT*\n`;
  message += `━━━━━━━━━━━━━━━━━━━━\n`;
  message += `📅 *Période:* Du ${startDate} au ${endDate}\n`;
  message += `💰 *État de paiement:* ${paymentStatus}\n\n`;

  // Add invoices/reports details
  if (reports && reports.length > 0) {
    message += `📋 *FACTURES:*\n`;
    message += `━━━━━━━━━━━━━━━━━━━━\n`;
    reports.forEach((report, index) => {
      message += `\n${index + 1}️⃣ *Réf:* ${report.reference || "N/A"}\n`;
      message += `   📅 *Date:* ${report.date || "N/A"}\n`;
      message += `   💵 *Total:* *${formatAmount(report.totalPriceTTC)} DH*\n`;
      message += `   ✅ *Payé:* ${formatAmount(report.amountPaid)} DH\n`;
      message += `   ❌ *Impayé:* *${formatAmount(report.amountUnpaid)} DH*\n`;

      // Add sale items if available
      if (report.sale_items && report.sale_items.length > 0) {
        report.sale_items.forEach((item) => {
          const itemName = 
            (item.item && item.item.fullName) || 
            item.itemFullName || 
            "N/A";
          const quantity = item.quantity || 0;
          const price = parseFloat(item.priceTTC || 0).toFixed(2);
          const total = formatAmount(item.totalPriceTTC);
          message += `   • 📦 ${itemName}: ${quantity} x ${price} = *${total} DH*\n`;
        });
      }
    });
    message += `\n`;
  }

  // Add payments details
  if (payments && payments.length > 0) {
    message += `💰 *PAIEMENTS:*\n`;
    message += `━━━━━━━━━━━━━━━━━━━━\n`;
    payments.forEach((payment) => {
      const paymentEmoji = payment.paymentMethod === "Chèque" ? "🏦" 
                          : payment.paymentMethod === "Espèce" ? "💵"
                          : payment.paymentMethod === "Virement" ? "💳"
                          : "💸";
      message += `${paymentEmoji} *${payment.reference || "N/A"}:* *${formatAmount(
        payment.amount
      )} DH*`;
      message += ` (${payment.date || "N/A"})`;
      message += ` - ${payment.paymentMethod || "N/A"}`;
      if (payment.paymentMethod === "Chèque" && payment.checkNumber) {
        message += ` N°${payment.checkNumber}`;
      }
      message += `\n`;
    });
    message += `\n`;
  }

  // Add credit balances
  if (creditBalances && creditBalances.length > 0) {
    message += `💳 *SOLDES CRÉDITEURS:*\n`;
    message += `━━━━━━━━━━━━━━━━━━━━\n`;
    creditBalances.forEach((credit) => {
      message += `🔴 *${credit.reference || "N/A"}:* -${formatAmount(
        credit.amount
      )} DH (${credit.date || "N/A"})\n`;
    });
    message += `\n`;
  }

  // Add summary totals
  message += `📊 *RÉSUMÉ:*\n`;
  message += `━━━━━━━━━━━━━━━━━━━━\n`;
  message += `💵 *Montant Total:* *${formatAmount(totalPriceTTC)} DH*\n`;
  message += `✅ *Montant Payé:* *${formatAmount(totalPaid)} DH*\n`;
  message += `❌ *Montant Impayé:* *${formatAmount(totalUnpaid)} DH*\n`;
  message += `💰 *Total Paiements:* *${formatAmount(totalReportPayments)} DH*\n`;

  const difference = parseFloat(totalReportPayments || 0) - parseFloat(totalPaid || 0);
  if (difference !== 0) {
    const diffEmoji = difference > 0 ? "⚠️" : "⚡";
    message += `${diffEmoji} *Différence:* *${formatAmount(difference)} DH*\n`;
  }

  message += `\n━━━━━━━━━━━━━━━━━━━━\n`;
  message += `🙏 Cordialement\n`;
  message += `📧 Peascom Admin`;

  return message;
}

/**
 * Share customer report via WhatsApp
 * @param {Object} params - Report data parameters
 * @param {Object} params.customer - Customer object with phone number
 * @param {Object} params.filter - Filter object
 * @param {Array} params.reports - Array of reports
 * @param {Array} params.payments - Array of payments
 * @param {Array} params.creditBalances - Array of credit balances
 * @param {number} params.totalPriceTTC - Total price TTC
 * @param {number} params.totalPaid - Total paid
 * @param {number} params.totalUnpaid - Total unpaid
 * @param {number} params.totalReportPayments - Total report payments
 * @param {Function} params.onError - Error callback function (optional)
 * @returns {boolean} - Returns true if WhatsApp was opened, false otherwise
 */
export function shareCustomerReportViaWhatsApp({
  customer,
  filter,
  reports,
  payments,
  creditBalances,
  totalPriceTTC,
  totalPaid,
  totalUnpaid,
  totalReportPayments,
  onError,
}) {
  // Validate customer and phone
  if (!customer || !customer.phone) {
    if (onError) {
      onError("Aucun numéro de téléphone trouvé pour ce client.");
    }
    return false;
  }

  // Validate report data
  if (!reports || reports.length === 0) {
    if (onError) {
      onError(
        "Aucun rapport disponible. Veuillez d'abord générer le rapport."
      );
    }
    return false;
  }

  // Format phone number
  const phone = formatPhoneNumber(customer.phone);
  if (!phone) {
    if (onError) {
      onError("Le numéro de téléphone n'est pas valide.");
    }
    return false;
  }

  // Build message
  const message = buildCustomerReportMessage({
    customer,
    filter,
    reports,
    payments,
    creditBalances,
    totalPriceTTC,
    totalPaid,
    totalUnpaid,
    totalReportPayments,
  });

  // Encode message for URL
  const encodedMessage = encodeURIComponent(message);

  // Open WhatsApp with the message
  const whatsappUrl = `https://wa.me/${phone}?text=${encodedMessage}`;
  window.open(whatsappUrl, "_blank");

  return true;
}

